import { Fragment } from "react";
import { Link, useLocation } from "react-router-dom";

import { NAV_ITEMS } from "@/shared/config/navigation";
import { cn } from "@/shared/lib/utils";

/**
 * Breadcrumbs derivadas del `pathname`. Resuelve el label de cada
 * segmento desde `NAV_ITEMS` cuando matchea, o lo humaniza como fallback.
 *
 * Diseño editorial:
 *   - Separador: punto medio tipográfico `·` (más suave que `/` o `>`).
 *   - Segmentos intermedios: muted + hover subtle.
 *   - Segmento actual: foreground + font-medium + `aria-current`.
 */
function labelForSegment(segment: string, fullPath: string): string {
  const hit = NAV_ITEMS.find((item) => item.to === fullPath);
  if (hit) return hit.label;
  // Fallback: humanizar — reemplazar guiones por espacios, capitalizar.
  return segment.replace(/-/g, " ").replace(/^./, (c) => c.toUpperCase());
}

export function Breadcrumbs() {
  const location = useLocation();
  const segments = location.pathname.split("/").filter(Boolean);

  // Siempre arrancamos con "Inicio" apuntando a /.
  const crumbs = [{ label: "Inicio", to: "/" }];
  segments.forEach((segment, i) => {
    const to = "/" + segments.slice(0, i + 1).join("/");
    if (to === "/") return;
    crumbs.push({ label: labelForSegment(segment, to), to });
  });

  return (
    <nav aria-label="Breadcrumb" className="min-w-0">
      <ol className="flex items-center gap-2 text-[13px]">
        {crumbs.map((crumb, i) => {
          const isLast = i === crumbs.length - 1;
          return (
            <Fragment key={crumb.to}>
              {i > 0 ? (
                <span aria-hidden className="select-none font-mono text-muted-foreground/40">
                  /
                </span>
              ) : null}
              {isLast ? (
                <span className="truncate font-medium text-foreground" aria-current="page">
                  {crumb.label}
                </span>
              ) : (
                <Link
                  to={crumb.to}
                  className={cn(
                    "truncate text-muted-foreground transition-colors hover:text-foreground",
                    "focus-visible:text-foreground focus-visible:outline-none",
                  )}
                >
                  {crumb.label}
                </Link>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
